import { isEmpty, isNil } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { currentAndUpcomingEvents } from '~admin/helper/event-list-data.predicate';
import { getEventsTaskFactory } from '~admin/interactor/event/perform-get-events';
import { EventListData } from '~admin/model/event-list-data';
import { ManageEventOverlay } from '~admin/view/components/view-events/manage-event-overlay.component';
import { Box } from '~common/components/box.component';
import { Column } from '~common/components/column.component';
import { Span } from '~common/components/span.component';
import { Title } from '~common/components/title.component';
import { useModalControls } from '~common/hook/use-modal-controls';
import { useSuccessReaction } from '~common/hook/use-reaction';
import { useTask } from '~common/hook/use-task';
import { addQuotes } from '~common/utils';
import { DataEmpty } from '~web/view/components/core/data-empty.component';
import { DataLoading } from '~web/view/components/core/data-loading.component';
import { getEventDate } from '~admin/view/components/view-events/view-events.helper';

interface Props {
  eventData: EventListData;
  onCardClick?: (eventData: EventListData) => void;
}

const EventCard: React.FunctionComponent<Props> = ({ eventData, onCardClick }) => {
  const { t } = useTranslation();
  const { announcement, eventName, defaultMedia } = eventData;

  return (
    <Card
      onClick={(): void => {
        onCardClick?.(eventData);
      }}
      background={defaultMedia.url}
    >
      <EventDetails>
        <EventName>{eventName}</EventName>
        <EventDate>{getEventDate(t, eventData)}</EventDate>
        {!isNil(announcement) && <Announcement>{announcement}</Announcement>}
      </EventDetails>
    </Card>
  );
};

export const ViewEvents: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const getEventsTask = useTask(getEventsTaskFactory);
  const [eventList, setEventList] = useState<EventListData[]>(undefined);
  const [selectedEvent, setSelectedEvent] = useState<EventListData>(undefined);
  const [overlayShown, showOverlay, hideOverlay] = useModalControls();

  useEffect((): void => {
    getEventsTask(true);
  }, []);

  useSuccessReaction(getEventsTask, (result: EventListData[]) => {
    setEventList(currentAndUpcomingEvents(result));
  });

  return (
    <Container>
      <Title>{t('viewEvents.title')}</Title>
      {isNil(eventList) && <DataLoading />}
      {isEmpty(eventList) && <DataEmpty title={t('viewEvents.noEvents')} />}
      <EventList>
        {eventList?.map((event: EventListData) => (
          <EventCard
            key={event.id}
            eventData={event}
            onCardClick={(eventData: EventListData): void => {
              setSelectedEvent(eventData);
              showOverlay();
            }}
          />
        ))}
      </EventList>
      {overlayShown && (
        <ManageEventOverlay
          eventId={selectedEvent?.id.toString()}
          vanityName={selectedEvent?.vanityName}
          onCancel={(): void => {
            setSelectedEvent(undefined);
            hideOverlay();
          }}
        />
      )}
    </Container>
  );
};

const Container = styled(Column)`
  flex: 1 1 auto;
  width: 100%;
  align-items: center;
  padding: 48px 35px 0;
`;

const EventList = styled(Column)`
  width: 100%;
  align-items: center;
  padding-bottom: 48px;
`;

const Card = styled(({ background, ...rest }) => <Box {...rest} />)<{
  background: string;
}>`
  width: 75%;
  max-width: 650px;
  min-height: 250px;
  align-items: flex-end;
  padding: 16px 16px 24px;
  margin: 5px 0;
  cursor: pointer;

  border-radius: 16px;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};

  background-image: url(${(props): string => addQuotes(props.background)});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top;

  &:hover {
    opacity: 0.88;
  }
`;

const EventDetails = styled(Box)`
  flex-direction: column;
  width: 100%;
  flex: 0 0 auto;
  padding: 0 8px;
  margin-bottom: 4px;
`;

const EventName = styled(Span)`
  font-size: 17px;
  line-height: 26px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-weight: bold;
`;

const EventDate = styled(Span)`
  font-size: 13px;
  letter-spacing: 0.3px;
  min-height: 18px;
  font-weight: bold;
`;

const Announcement = styled(EventDate)`
  font-style: italic;
  color: ${(props): string => props.theme.colors.withAlpha(props.theme.colors.white)(0.8)};
`;



// WEBPACK FOOTER //
// ./view/components/view-events/view-events.component.tsx